// PublisherLedger.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    UncontrolledTooltip,
    Table,
    Spinner,
    CardHeader,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FaDownload,
    FaEye,
    FaExclamationTriangle,
    FaHistory,
    FaFilePdf,
    FaFileExcel,
    FaPlus,
    FaEdit,
    FaTrash,
    FaCheck,
    FaTimes,
    FaInfoCircle,
} from "react-icons/fa";
import { format } from "date-fns";
import ReactBsTable from "components/Tables/ReactBsTable";
import NotificationAlert from "react-notification-alert";
import { ledgerListForPublisher, uploadInvoice } from "actions/ledger";
import { MAX_FILE_SIZE } from "variables/constants";
import { getActiveLedgerDetails } from "actions/ledger";
import { listInvoicesHistory } from "actions/ledger";
import ReactPlainTable from "components/Tables/ReactPlainTable";
import { downloadInvoice } from "actions/ledger";

// =============== MODAL STYLES ===============
const modalStyles = {
    header: {
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "18px",
        paddingTop: "18px",
    },
    title: {
        fontSize: "18px",
        fontWeight: "700",
        color: "#2c3e50",
        letterSpacing: "0.3px",
    },
    body: {
        padding: "24px",
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
    },
};

// =============== HELPER FUNCTIONS ===============
const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);
};

const getStatusBadge = (status) => {
    const statusMap = {
        open: { color: "warning", label: "Open" },
        POSTED: { color: "info", label: "Posted" },
        INVOICED: { color: "primary", label: "Invoiced" },
        PAID: { color: "success", label: "Paid" },
        DRAFT: { color: "secondary", label: "Draft" },
        LOCK: { color: "primary", label: "Locked" },
        RERUN: { color: "danger", label: "Rerun" },
        PENDING: { color: "warning", label: "Pending" },
        ACCEPTED: { color: "success", label: "Accepted" },
        REJECTED: { color: "danger", label: "Rejected" },
        VOID: { color: "secondary", label: "Void" },
    };
    const s = statusMap[status] || statusMap.open;
    return <Badge color={s.color}>{s.label}</Badge>;
};

const getInvoiceStatusBadge = (status) => {
    const statusMap = {
        PENDING: { color: "warning", label: "Pending" },
        ACCEPTED: { color: "success", label: "Accepted" },
        REJECTED: { color: "danger", label: "Rejected" },
        VOID: { color: "secondary", label: "Void" },
        OPEN: { color: "warning", label: "Open" },
        PAID: { color: "success", label: "Paid" },
    };
    const s = statusMap[status];
    return s ? (
        <Badge color={s.color}>{s.label}</Badge>
    ) : (
        <span className="text-muted">NA</span>
    );
};

const monthPickerStyles = `
        .styled-month-input {
            border: 1px solid #1336a0 !important;
            border-radius: 12px !important;
            height: 44px !important;
            padding: 0.75rem 0.9rem !important;
            font-size: 0.95rem !important;
            background: linear-gradient(180deg, #ff0000 0%, #f8faff 100%) !important;
            box-shadow: 0 2px 8px rgba(185, 185, 185, 0.08) !important;
            transition: all 0.2s ease !important;
        }
        .styled-month-input:focus {
            border-color: #b7b7b7 !important;
            box-shadow: 0 0 0 4px rgba(108, 108, 109, 0.12) !important;
            outline: none !important;
        }
        .styled-month-picker {
            border: none !important;
            border-radius: 16px !important;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16) !important;
            overflow: hidden !important;
            background: #ffffff !important;
        }
        .styled-month-picker .react-datepicker__header {
            background: linear-gradient(135deg, #3b3a3a 0%, #8a8a8a 100%) !important;
            border-bottom: none !important;
            padding-top: 14px !important;
        }
        .styled-month-picker .react-datepicker__current-month,
        .styled-month-picker .react-datepicker-time__header,
        .styled-month-picker .react-datepicker-year-header {
            color: #ffffff !important;
            font-weight: 700 !important;
        }
        .styled-month-picker .react-datepicker__navigation {
            top: 7px !important;
               align-items: center !important;
              
        }
        .styled-month-picker .react-datepicker__navigation-icon::before {
            border-color: #ffffff !important;
               align-items: center !important;
              
        }
        .styled-month-picker .react-datepicker__month-text {
            width: 88px !important;
            padding: 12px 0 !important;
            border-radius: 10px !important;
            margin: 5px 0 !important;
            font-size: 0.9rem !important;
            color: #243b53 !important;
        }
        .styled-month-picker .react-datepicker__month-text:hover {
            background: #cdcdcd !important;
            color: #121212 !important;
        }
        .styled-month-picker .react-datepicker__month-text--selected,
        .styled-month-picker .react-datepicker__month-text--keyboard-selected {
            background: linear-gradient(135deg, #a2a2a2 0%, #1a1818 100%) !important;
            color: #ffffff !important;
            box-shadow: 0 8px 18px rgba(143, 143, 143, 0.22) !important;
        }
        .styled-month-picker-popper {
            z-index: 1300 !important;
        }
    `;
// =============== COMPONENT ===============
const PublisherLedger = () => {
    const notificationAlertRef = useRef(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedLedger, setSelectedLedger] = useState(null);
    const [displayedLedgers, setDisplayedLedgers] = useState([]);
    const [displayedLedgersHistory, setDisplayedLedgersHistory] = useState([]);
    const [historyTotalRecord, setHistoryTotalRecord] = useState(0);
    const [activeInvoice, setActiveInvoice] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [error, setError] = useState(null);
    const [currentFilterMonth, setCurrentFilterMonth] = useState(null);
    const [currentFilterYear, setCurrentFilterYear] = useState(null);

    const truncateText = (text, maxLength = 30) => {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    // Modal states
    const [isRerunModalOpen, setIsRerunModalOpen] = useState(false);
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    const [uploadForm, setUploadForm] = useState({
        publisherId: "",
        publisherName: "",
        ledgerId: "",
        invoiceNumber: "",
        invoiceFile: null,
        error: null,
    });

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem("p"));
    const publisherID = userData?.id || 1;

    // =============== NOTIFICATIONS ===============
    const notify = (type, message) => {
        if (notificationAlertRef.current) {
            notificationAlertRef.current.notificationAlert({
                place: "tr",
                message: <div>{message}</div>,
                type,
                icon: "tim-icons icon-bell-55",
                autoDismiss: 5,
            });
        }
    };

    // Helper function to get month display name
    const getMonthDisplay = (monthNumber, year) => {
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const currentYear = year || new Date().getFullYear();
        return `${monthNames[monthNumber - 1]} ${currentYear}`;
    };

    // =============== FETCH LEDGER DATA ===============
    const fetchLedgerList = async (page = 1, month = null, year = null) => {
        try {
            setIsLoading(true);
            setError(null);

            let monthNumber = month;
            let yearNumber = year;
            
           
            
            setCurrentFilterMonth(monthNumber);
            setCurrentFilterYear(yearNumber);
            
            const response = await ledgerListForPublisher(page, monthNumber, yearNumber);
            let ledgerData = [];
            let totalRecordsCount = 0;

            if (response) {
                if (Array.isArray(response)) {
                    ledgerData = response;
                    totalRecordsCount = response.length;
                } else if (response.data) {
                    if (Array.isArray(response.data)) {
                        ledgerData = response.data;
                        totalRecordsCount = response.totalRecords || response.data.length;
                    } else if (response.data.Ledger && Array.isArray(response.data.Ledger)) {
                        ledgerData = response.data.Ledger;
                        totalRecordsCount = response.data.totalRecords || ledgerData.length;
                    } else if (response.data.ledger && Array.isArray(response.data.ledger)) {
                        ledgerData = response.data.ledger;
                        totalRecordsCount = response.data.totalRecords || ledgerData.length;
                    } else {
                        const values = Object.values(response.data);
                        for (const val of values) {
                            if (Array.isArray(val) && val.length > 0) {
                                ledgerData = val;
                                totalRecordsCount = val.length;
                                break;
                            }
                        }
                    }
                } else if (response.totalRecords !== undefined) {
                    totalRecordsCount = response.totalRecords;
                    for (const key of Object.keys(response)) {
                        if (Array.isArray(response[key]) && key !== 'totalRecords' && key !== 'noOfRecordsPerPage') {
                            ledgerData = response[key];
                            break;
                        }
                    }
                }
            }

            const mappedLedgers = Array.isArray(ledgerData) && ledgerData.length > 0
                ? ledgerData.map((item, index) => {
                    const id = item.ID || item.id || index + 1;
                    const revenue = parseFloat(item.revenue || item.Revenue || 0);
                    const netPayable = parseFloat(item.netPayable || item.NetPayable || 0);
                    const totalPayable = parseFloat((item.netPayable || item.NetPayable || 0) - (item.advanceApplied || item.AdvanceApplied || 0) || item.TotalPayable || 0);
                    const advanceApplied = parseFloat(item.advanceApplied || item.AdvanceApplied || 0);
                    const status = item.status || item.Status || "open";
                    const currency = item.currency || item.Currency || "USD";
                    const postedBy = item.postedBy || item.PostedBy || "";
                    const postedAt = item.postedAt || item.PostedAt || null;
                    const lockedAt = item.lockedAt || item.LockedAt || null;
                    const uploadedAt = item.uploadedAt|| "-"
                    const adjustment = item.adjustment || item.Adjustment || {};
                    const adjustmentType = adjustment.adjustmentType || adjustment.type || "";
                    const adjustmentAmount = parseFloat(adjustment.amount || adjustment.Amount || 0);
                    const adjustmentDescription = adjustment.description || adjustment.Description || "";
                    
                    const invoiceInfo = item.invoiceInfo || {};
                    
                    const displayMonth = monthNumber;
                    const displayYear = yearNumber;
                    
                    
                    const monthKey = `${displayYear}-${String(displayMonth).padStart(2, '0')}`;

                    const isLocked = status === "LOCK" || status === "POSTED" || status === "lock" || status === "posted";

                    return {
                        id: id,
                        publisherId: item.publisherID || item.publisherId || publisherID,
                        publisherName: item.publisherName ||"-",
                        month: monthKey,
                        monthDisplay: formatMonthDisplay(item.ledgerMonth),
                        revenue: revenue,
                        adjustments: adjustmentAmount,
                        adjustmentType: adjustmentType,
                        adjustmentDescription: adjustmentDescription,
                        adjustmentID: adjustment.ID || adjustment.id || 0,
                        adjustmentsList: adjustmentAmount > 0 ? [{
                            id: adjustment.ID || adjustment.id || 0,
                            type: adjustmentType,
                            amount: adjustmentAmount,
                            description: adjustmentDescription,
                            createdAt: new Date().toISOString(),
                            user: postedBy || "Admin"
                        }] : [],
                        advanceApplied: advanceApplied,
                        netPayable: netPayable,
                        totalPayable: totalPayable,
                        uploadedAt: uploadedAt,
                        status: status,
                        invoiceId: invoiceInfo.id || null,
                        invoiceNumber: invoiceInfo.invoiceNumber || null,
                        invoiceStatus: invoiceInfo.status || null,
                        invoicePath: invoiceInfo.invoicePath || null,
                        issueDate: invoiceInfo.issueDate || null,
                        dueDate: invoiceInfo.dueDate || null,
                        isLocked: isLocked,
                        postedAt: postedAt,
                        postedBy: postedBy,
                       
                        currency: currency,
                      
                        openingBalance: parseFloat(item.openingBalance || item.OpeningBalance || 0),
                        closingBalance: parseFloat(item.closingBalance || item.ClosingBalance || 0),
                        adjustmentTotal: parseFloat(item.adjustmentTotal || item.AdjustmentTotal || 0),
                        lockedAt: lockedAt,
                    };
                })
                : [];
            setDisplayedLedgers(mappedLedgers);
            setTotalRecords(totalRecordsCount || mappedLedgers.length);

        } catch (error) {
            console.error("Error fetching ledger data:", error);
            setError(error.message);
            notify("danger", "Failed to fetch ledger data: " + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // =============== FETCH ACTIVE INVOICE ===============
    const fetchLedgerHistoryList = async (page = 1, month = null, year = null) => {
        try {
            setIsLoading(true);
            setError(null);

            let monthNumber = month;
            let yearNumber = year;
            
           
            
            setCurrentFilterMonth(monthNumber);
            setCurrentFilterYear(yearNumber);
            
            const response = await listInvoicesHistory(page, monthNumber, yearNumber);
            let ledgerData = [];
            let totalRecordsCount = 0;

            if (response) {
                if (Array.isArray(response)) {
                    ledgerData = response;
                    totalRecordsCount = response.length;
                } else if (response.data) {
                    if (Array.isArray(response.data)) {
                        ledgerData = response.data;
                        totalRecordsCount = response.totalRecords || response.data.length;
                    } else if (response.data.Ledger && Array.isArray(response.data.Ledger)) {
                        ledgerData = response.data.Ledger;
                        totalRecordsCount = response.data.totalRecords || ledgerData.length;
                    } else if (response.data.ledger && Array.isArray(response.data.ledger)) {
                        ledgerData = response.data.ledger;
                        totalRecordsCount = response.data.totalRecords || ledgerData.length;
                    } else {
                        const values = Object.values(response.data);
                        for (const val of values) {
                            if (Array.isArray(val) && val.length > 0) {
                                ledgerData = val;
                                totalRecordsCount = val.length;
                                break;
                            }
                        }
                    }
                } else if (response.totalRecords !== undefined) {
                    totalRecordsCount = response.totalRecords;
                    for (const key of Object.keys(response)) {
                        if (Array.isArray(response[key]) && key !== 'totalRecords' && key !== 'noOfRecordsPerPage') {
                            ledgerData = response[key];
                            break;
                        }
                    }
                }
            }

            const mappedLedgers = Array.isArray(ledgerData) && ledgerData.length > 0
                ? ledgerData.map((item, index) => {
                    const id = item.ID || item.id || index + 1;
                    const revenue = parseFloat(item.revenue || item.Revenue || 0);
                    const netPayable = parseFloat(item.netPayable || item.NetPayable || 0);
                    const totalPayable = parseFloat((item.netPayable || item.NetPayable || 0) - (item.advanceApplied || item.AdvanceApplied || 0) || item.TotalPayable || 0);
                    const advanceApplied = parseFloat(item.advanceApplied || item.AdvanceApplied || 0);
                    const status = item.status || item.Status || "open";
                    const currency = item.currency || item.Currency || "USD";
                    const postedBy = item.postedBy || item.PostedBy || "";
                    const postedAt = item.postedAt || item.PostedAt || null;
                    const lockedAt = item.lockedAt || item.LockedAt || null;
                    const uploadedAt = item.uploadedAt || "-"
                    const adjustment = item.adjustment || item.Adjustment || {};
                    const adjustmentType = adjustment.adjustmentType || adjustment.type || "";
                    const adjustmentAmount = parseFloat(adjustment.amount || adjustment.Amount || 0);
                    const adjustmentDescription = adjustment.description || adjustment.Description || "";
                    
                    const invoiceInfo = item.invoiceInfo || {};
                    
                    const displayMonth = monthNumber;
                    const displayYear = yearNumber;
                    
                    
                    const monthKey = `${displayYear}-${String(displayMonth).padStart(2, '0')}`;

                    const isLocked = status === "LOCK" || status === "POSTED" || status === "lock" || status === "posted";

                    return {
                        id: id,
                        publisherId: item.publisherID || item.publisherId || publisherID,
                        publisherName: item.publisherName ||"-",
                        month: monthKey,
                        monthDisplay: formatMonthDisplay(item.ledgerMonth),
                        revenue: revenue,
                        adjustments: adjustmentAmount,
                        adjustmentType: adjustmentType,
                        adjustmentDescription: adjustmentDescription,
                        adjustmentID: adjustment.ID || adjustment.id || 0,
                        adjustmentsList: adjustmentAmount > 0 ? [{
                            id: adjustment.ID || adjustment.id || 0,
                            type: adjustmentType,
                            amount: adjustmentAmount,
                            description: adjustmentDescription,
                            createdAt: new Date().toISOString(),
                            user: postedBy || "Admin"
                        }] : [],
                        advanceApplied: advanceApplied,
                        netPayable: netPayable,
                        totalPayable: totalPayable,
                        uploadedAt:uploadedAt,
                        status: status,
                        invoiceId: invoiceInfo.id || null,
                        invoiceNumber: invoiceInfo.invoiceNumber || null,
                        invoiceStatus: invoiceInfo.status || null,
                        invoicePath: invoiceInfo.invoicePath || null,
                        issueDate: invoiceInfo.issueDate || null,
                        dueDate: invoiceInfo.dueDate || null,
                        isLocked: isLocked,
                        postedAt: postedAt,
                        postedBy: postedBy,
                       
                        currency: currency,
                      
                        openingBalance: parseFloat(item.openingBalance || item.OpeningBalance || 0),
                        closingBalance: parseFloat(item.closingBalance || item.ClosingBalance || 0),
                        adjustmentTotal: parseFloat(item.adjustmentTotal || item.AdjustmentTotal || 0),
                        lockedAt: lockedAt,
                    };
                })
                : [];
            setDisplayedLedgersHistory(mappedLedgers);
            setHistoryTotalRecord(totalRecordsCount || mappedLedgers.length);

        } catch (error) {
            console.error("Error fetching ledger data:", error);
            setError(error.message);
            notify("danger", "Failed to fetch ledger data: " + error.message);
        } finally {
            setIsLoading(false);
        }
    };


    const formatMonthDisplay = (monthString) => {
        if (!monthString) return "";
        try {
            const [year, month] = monthString.split("-");
            if (!year || !month) return monthString;
            const date = new Date(parseInt(year), parseInt(month) - 1, 1);
            return format(date, "MMMM yyyy");
        } catch (e) {
            return monthString;
        }
    };

    // =============== EFFECTS ===============
    useEffect(() => {
        fetchLedgerList(pageNum);
    }, []);

    // =============== HANDLERS ===============
    const handleMonthChange = (date) => {
        if (date) {
            const monthNumber = date.getMonth() + 1;
            const year = date.getFullYear();
            setSelectedMonth(monthNumber);
            setSelectedYear(year);
            fetchLedgerList(1, monthNumber, year);
        } else {
            setSelectedMonth(null);
            fetchLedgerList(1, )
        }
    };

    const handlePageChange = (page) => {
        setPageNum(page);
        const monthNumber = currentFilterMonth || (selectedMonth || new Date().getMonth() + 1);
        const yearNumber = currentFilterYear || (selectedYear || new Date().getFullYear());
        fetchLedgerList(page, monthNumber, yearNumber);
    };

    const handleSizePerPageChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setPageNum(1);
        const monthNumber = currentFilterMonth || (selectedMonth || new Date().getMonth() + 1);
        const yearNumber = currentFilterYear || (selectedYear || new Date().getFullYear());
        fetchLedgerList(1, monthNumber, yearNumber);
    };

    const handleReset = () => {
        setSelectedMonth(null);
        setSelectedYear(null);
        setPageNum(1);
        fetchLedgerList(1, );

    };

const handleViewDetails = (row) => {

    setIsDetailsModalOpen(true)
    // Extract month and year from the row data
    let monthToSend = null;
    let yearToSend = null;
    
    // Check if we have monthDisplay or month field
    if (row.monthDisplay) {
        // Parse monthDisplay like "May 2026"
        const monthParts = row.monthDisplay.split(' ');
        if (monthParts.length === 2) {
            const monthName = monthParts[0];
            const year = parseInt(monthParts[1]);
            
            // Convert month name to number
            const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];
            const monthIndex = monthNames.indexOf(monthName);
            if (monthIndex !== -1) {
                monthToSend = monthIndex + 1; // Month is 1-indexed
                yearToSend = year;
            }
        }
    } else if (row.month && row.month !== "null-null") {
        // If month is in format "YYYY-MM"
        const monthParts = row.month.split('-');
        if (monthParts.length === 2) {
            yearToSend = parseInt(monthParts[0]);
            monthToSend = parseInt(monthParts[1]);
        }
    }
    
    // If we couldn't extract month/year, use current month/year
    if (!monthToSend || !yearToSend) {
        const currentDate = new Date();
        monthToSend = currentDate.getMonth() + 1;
        yearToSend = currentDate.getFullYear();
    }
    
    // Call the API with month and year
    fetchLedgerHistoryList(pageNum, monthToSend, yearToSend);
};

  // Handle download
const downloadFile = (blobData, fileName, contentType) => {
    const blob = new Blob([blobData], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");

    link.href = url;
    link.download = fileName;

    window.document.body.appendChild(link);
    link.click();

    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const handleDownloadInvoice = async (row) => {
    console.log(row);
    try {
        const res = await downloadInvoice(row.invoiceId);

        // Check if response is an error (JSON object with code/message)
        if (res?.code && (res?.code !== 200)) {
            const message = Array.isArray(res?.message)
                ? res?.message[0]?.Msg
                : res?.message || "Something went wrong";
            notify("danger", message);
            return;
        }

        // Determine filename and content type
        let fileName = row.invoiceNumber || "invoice";
        let contentType = "application/octet-stream";

        // Try to get file extension from the invoice path/url
        const invoicePath = row.invoicePath || row.InvoicePath || "";
        if (invoicePath) {
            const pathParts = invoicePath.split("/");
            const fullFileName = pathParts[pathParts.length - 1]; // e.g. "3_testing.png"
            
            if (fullFileName) {
                // Extract the extension from the path
                const extension = fullFileName.split(".").pop().toLowerCase();
                fileName = `${fileName}.${extension}`;

                // Determine content type based on extension
                switch (extension) {
                    case "pdf":
                        contentType = "application/pdf";
                        break;
                    case "png":
                        contentType = "image/png";
                        break;
                    case "jpg":
                    case "jpeg":
                        contentType = "image/jpeg";
                        break;
                    case "gif":
                        contentType = "image/gif";
                        break;
                    case "doc":
                        contentType = "application/msword";
                        break;
                    case "docx":
                        contentType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                        break;
                    default:
                        contentType = "application/octet-stream";
                }
            }
        }

        // If response is binary (Blob or ArrayBuffer), download it
        if (res instanceof Blob || res instanceof ArrayBuffer || typeof res === "string") {
            downloadFile(res, fileName, contentType);
            notify("success", `Downloading ${fileName}`);
        } else {
            notify("danger", "Invalid file response");
        }
    } catch (error) {
        console.error("Download error:", error);
        notify("danger", "Failed to download invoice");
    }
};



    // =============== TABLE COLUMNS ===============
    const columns = [
        {
            dataField: "monthDisplay",
            text: "Month",
            sort: true,
            formatter: (cell) => cell || "-",
        },
        {
            dataField: "invoiceNumber",
            text: "Invoice Number",
            formatter: (cell, row) => {
                if (row.invoiceNumber && row.invoiceNumber !== "") {
                    return (
                        <a
                            href="#"
                            style={{ color: '#1a3c6e', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}
                               onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              setSelectedLedger(row);
                                        setIsInvoiceModalOpen(true);
                            }}
                        >
                            {row.invoiceNumber}
                        </a>
                    );
                }
                return <span className="text-muted">NA</span>;
            },
            sort: true,
        },
        {
            dataField: "invoiceStatus",
            text: "Invoice Status",
            formatter: (cell) => getInvoiceStatusBadge(cell),
            sort: true,
        },
        {
            dataField: "revenue",
            text: "Revenue",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "adjustments",
            text: "Adjustments",
            formatter: (cell, row) => (
                <div>
                    {cell !== 0 ? (
                        <span className={cell > 0 ? "text-success" : "text-danger"}>
                            {formatCurrency(cell)}
                        </span>
                    ) : "-"}
                </div>
            ),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "adjustmentDescription",
            text: "Description",
            formatter: (cell) => {
                if (!cell) return <span className="text-muted">No description</span>;
                const truncated = truncateText(cell, 30);
                if (truncated === cell) {
                    return <span>{cell}</span>;
                }
                const uniqueId = `desc-${Math.random().toString(36).substr(2, 9)}`;
                return (
                    <>
                        <span
                            id={uniqueId}
                            style={{ cursor: 'pointer', borderBottom: '1px dashed #ccc' }}
                        >
                            {truncated}
                        </span>
                        <UncontrolledTooltip delay={0} target={uniqueId}>
                            {cell}
                        </UncontrolledTooltip>
                    </>
                );
            },
            sort: true,
            headerAlign: "left",
            align: "left",
        },
       
        {
            dataField: "netPayable",
            text: "Net Revenue",
            formatter: (cell) => <span className="fw-bold">{formatCurrency(cell)}</span>,
            headerAlign: "right",
            align: "right",
            sort: true,
        },
         {
            dataField: "advanceApplied",
            text: "Advance Applied",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "totalPayable",
            text: "Final Revenue",
            formatter: (cell) => <span className="fw-bold">{formatCurrency(cell)}</span>,
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "status",
            text: "Ledger Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
            headerAlign: "center",
            align: "center",
        },
                {
            dataField: "uploadedAt",
            text: "Uploaded At",
             formatter: (cell) => cell || "-",
            sort: true,
            headerAlign: "center",
            align: "center",
        },
        {
            dataField: "actions",
            text: "Actions",
            formatter: (cell, row) => {
                const rowId = row.id;
                const hasInvoice = row.invoiceNumber && row.invoiceNumber !== "";
                const isLocked = row.isLocked || row.status === "LOCK" || row.status === "POSTED" || row.status === "PAID";
                
                return (
                    <div style={{ display: "flex", gap: "4px", justifyContent: "center", flexWrap: "wrap" }}>
                        {hasInvoice && (
                            <>
                             <a
                            className="table-action table-action-info"
                            id={`details-${rowId}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(row);
                            }}
                        >
                            <i className="fas fa-info-circle" />
                        </a>
                        <UncontrolledTooltip delay={0} target={`details-${rowId}`}>
                            View Details
                        </UncontrolledTooltip>
                            </>
                        )}
                       
                        
                        {hasInvoice && (
                            <>
                                <a
                                    className="table-action table-action-info"
                                    id={`view-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLedger(row);
                                        setIsInvoiceModalOpen(true);
                                    }}
                                >
                                    <i className="fas fa-eye" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`view-${rowId}`}>
                                    View Invoice
                                </UncontrolledTooltip>
                                <a
                                    className="table-action table-action-info"
                                    id={`download-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                      handleDownloadInvoice(row)
                                    }}
                                >
                                    <i className="fas fa-download" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`download-${rowId}`}>
                                    Download Invoice
                                </UncontrolledTooltip>
                            </>
                        )}
                        
                       {row.invoiceStatus !== "PAID" && row.totalPayable > 0 && (

                            <>
                                <a
                                    className="table-action table-action-info"
                                    id={`upload-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLedger(row);
                                        setUploadForm({
                                            publisherId: row.publisherId || row.id || publisherID,
                                            publisherName: row.publisherName || "",
                                            ledgerId: row.ledgerID || row.ledgerId || row.id || "",
                                            invoiceNumber: "",
                                            invoiceFile: null,
                                            error: null,
                                        });
                                        setIsUploadModalOpen(true);
                                    }}
                                >
                                    <i className="fas fa-file-upload" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`upload-${rowId}`}>
                                    Upload Invoice
                                </UncontrolledTooltip>
                            </>
                       )}
                    
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        }
    ];

     const historyColumns = [
        {
            dataField: "monthDisplay",
            text: "Month",
            sort: true,
            formatter: (cell) => cell || "-",
        },
        {
            dataField: "invoiceNumber",
            text: "Invoice Number",
            formatter: (cell, row) => {
                if (row.invoiceNumber && row.invoiceNumber !== "") {
                    return (
                        <a
                            href="#"
                            style={{ color: '#1a3c6e', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              setSelectedLedger(row);
                                        setIsInvoiceModalOpen(true);
                            }}
                        >
                            {row.invoiceNumber}
                        </a>
                    );
                }
                return <span className="text-muted">NA</span>;
            },
            sort: true,
        },
        {
            dataField: "invoiceStatus",
            text: "Invoice Status",
            formatter: (cell) => getInvoiceStatusBadge(cell),
            sort: true,
        },
       {
            dataField: "uploadedAt",
            text: "Uploaded At",
             formatter: (cell) => cell || "-",
            sort: true,
            headerAlign: "center",
            align: "center",
        },
        {
            dataField: "status",
            text: "Ledger Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
            headerAlign: "center",
            align: "center",
        },
     
    ];

    // =============== FILTER SECTION ===============
    const filterSection = () => (
        <Row className="align-items-center">
            <Col md={3}>
                <FormGroup>
                    <DatePicker
                        selected={selectedMonth ? new Date(selectedYear, selectedMonth - 1, 1) : null}
                        onChange={handleMonthChange}
                        className="form-control"
                        calendarClassName="styled-month-picker"
                        popperClassName="styled-month-picker-popper"
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        placeholderText="Select Month / Year"
                        isClearable
                    />
                </FormGroup>
            </Col>
            <Col md={3}>
                <Button className="secondary" onClick={handleReset}>
                    Reset
                </Button>
            </Col>
            {isLoading && (
                <Col md={3}>
                    <Spinner size="sm" color="primary" />
                </Col>
            )}
        </Row>
    );

    // =============== RENDER ===============
    return (
        <>
            <style>{monthPickerStyles}</style>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <Container className="mt--6" fluid>
                {/* Ledger Table */}
           
                {/* Active Invoice Details Card */}
                {/* <Card className="mt-4">
                    <CardHeader>
                        <h3 className="mb-0">Active Invoice Details</h3>
                    </CardHeader>
                    <CardBody>
                       
                          
                            
                     
                        {isInvoiceLoading ? (
                            <div className="text-center py-4">
                                <Spinner color="primary" />
                            </div>
                        ) : activeInvoice ? (
                            <div>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Invoice Number</Label>
                                            <div className="font-weight-bold">{activeInvoice.invoiceNumber || "-"}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Status</Label>
                                            <div>{getInvoiceStatusBadge(activeInvoice.status)}</div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Publisher</Label>
                                            <div className="font-weight-bold">{activeInvoice.companyName || "-"}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Month</Label>
                                            <div className="font-weight-bold">{activeInvoice.monthDisplay || "-"}</div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Revenue</Label>
                                            <div className="font-weight-bold">{formatCurrency(activeInvoice.revenue)}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Adjustments</Label>
                                            <div className="font-weight-bold">{formatCurrency(activeInvoice.adjustmentTotal || 0)}</div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Advance Applied</Label>
                                            <div className="font-weight-bold">{formatCurrency(activeInvoice.advanceApplied || 0)}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Net Payable</Label>
                                            <div className="font-weight-bold text-primary">{formatCurrency(activeInvoice.netPayable)}</div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Opening Balance</Label>
                                            <div className="font-weight-bold">{formatCurrency(activeInvoice.openingBalance || 0)}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Total Payout</Label>
                                            <div className="font-weight-bold text-success">{formatCurrency(activeInvoice.netPayable - activeInvoice.advanceApplied || 0)}</div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Uploaded By</Label>
                                            <div className="font-weight-bold">{activeInvoice.uploadedBy || "-"}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="text-muted small text-uppercase">Created At</Label>
                                            <div className="font-weight-bold">
                                                {activeInvoice.createdAt ? format(new Date(activeInvoice.createdAt), "MMM dd, yyyy hh:mm a") : "-"}
                                            </div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {activeInvoice.rejectedReason && (
                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label className="text-muted small text-uppercase text-danger">Rejected Reason</Label>
                                                <div className="font-weight-bold text-danger">{activeInvoice.rejectedReason}</div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                )}

                                <Row className="mt-3">
                                    <Col>
                                        {activeInvoice.invoicePath && (
                                            <>
                                                <Button
                                                    color="primary"
                                                    className="filter-btn-gradient-primary me-2"
                                                    onClick={() => handleDownloadInvoice(activeInvoice.invoicePath, activeInvoice.invoiceNumber)}
                                                >
                                                    <FaEye className="me-1" /> View Invoice
                                                </Button>
                                                <Button
                                                    color="secondary"
                                                    onClick={() => handleDownloadInvoice(activeInvoice.invoicePath, activeInvoice.invoiceNumber)}
                                                >
                                                    <FaDownload className="me-1" /> Download
                                                </Button>
                                            </>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            <div className="text-center py-3 text-muted">
                                No active invoice found for the selected period
                            </div>
                        )}
                    </CardBody>
                </Card> */}

                {/* Details Modal */}
                <Modal isOpen={isDetailsModalOpen} toggle={() => setIsDetailsModalOpen(false)} centered size="xl">
                    <ModalHeader toggle={() => setIsDetailsModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                           Invoice History
                        </h5>
                    </ModalHeader>
                   
                       <ReactPlainTable
                        data={displayedLedgersHistory}
                        showAddbtn={false}
                        columns={historyColumns}
                        keyField="id"
                        // renderFilters={filterSection}
                        page={pageNum}
                        sizePerPage={pageSize}
                        onexportCsvBtnClick={() => setIsExportModalOpen(true)}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        showLoader={isLoading}
                        enablePageSizeManagement={false}
                        bordered
                        hover
                    />
                  
                    <ModalFooter>
                        
                        <Button color="secondary" onClick={() => setIsDetailsModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Invoice Preview Modal */}
                <Modal isOpen={isInvoiceModalOpen} toggle={() => setIsInvoiceModalOpen(false)} centered size="xl">
                    <ModalHeader toggle={() => setIsInvoiceModalOpen(false)} style={modalStyles.header}>
                        <h3 style={modalStyles.title}>Tax Invoice</h3>
                    </ModalHeader>
                    <ModalBody style={{ ...modalStyles.body, overflow: 'hidden' }}>
                        {selectedLedger && (
                            <div className="invoice-preview p-4">
                                {selectedLedger?.invoicePath && (
                                    <div style={{ height: '70vh', marginTop: 16, overflow: 'hidden' }}>
                                        <iframe
                                            src={selectedLedger.invoicePath}
                                            title="Invoice Preview"
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                        />
                                    </div>
                                )}
                                <Row className="mt-2">
                                    <Col>
                                        <p className="text-muted" style={{ fontSize: '11px', marginBottom: '0' }}>
                                            * This invoice is generated automatically by TrueLift system. VAT is charged at 5% for UAE supplies.
                                        </p>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsInvoiceModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* ========== UPLOAD INVOICE MODAL ========== */}
                <Modal isOpen={isUploadModalOpen} toggle={() => setIsUploadModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsUploadModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Upload Invoice</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Form>
                            <FormGroup>
                                <Label>Invoice Number <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    placeholder="Enter invoice number"
                                    value={uploadForm.invoiceNumber}
                                    onChange={(e) => setUploadForm({ ...uploadForm, invoiceNumber: e.target.value })}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Upload Invoice File <span className="text-danger">*</span></Label>
                                <div
                                    className={`product-image-upload w-100 p-3 rounded text-center ${uploadForm.error ? "border border-warning" : "border"}`}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDragEnter={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const file = e.dataTransfer.files[0];
                                        if (!file) return;
                                        const allowedTypes = [
                                            "application/pdf",
                                            "application/msword",
                                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                            "image/png",
                                            "image/jpeg",
                                            "image/jpg",
                                            "image/gif",
                                        ];
                                        if (!allowedTypes.includes(file.type)) {
                                            setUploadForm((prev) => ({ ...prev, error: "Unsupported file type" }));
                                            return;
                                        }
                                        if (file.size > MAX_FILE_SIZE) {
                                            setUploadForm((prev) => ({ ...prev, error: "File size should not exceed 10 MB" }));
                                            return;
                                        }
                                        setUploadForm((prev) => ({ ...prev, invoiceFile: file, error: null }));
                                    }}
                                >
                                    {uploadForm?.invoiceFile?.name ? (
                                        <div className="border rounded p-3">
                                            <div className="d-flex flex-column align-items-start">
                                                <div className="d-flex align-items-start">
                                                    <i className="fa fa-file-pdf text-danger fa-4x mb-2 ml-4" />
                                                    <span
                                                        className="text-danger pointer ml-2"
                                                        onClick={() => setUploadForm((prev) => ({ ...prev, invoiceFile: null }))}
                                                    >
                                                        <i className="fa fa-times" />
                                                    </span>
                                                </div>
                                                <p className="mb-2">{uploadForm.invoiceFile.name}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column align-items-center">
                                            <i className="fa fa-cloud-upload fa-2x text-muted mb-2" />
                                            <p className="mb-1 text-muted">Click or drag document to upload</p>
                                            <Label for="publisherUploadFileInput" className="btn btn-simple btn-sm mt-2">Choose Document</Label>
                                        </div>
                                    )}

                                    <Input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,image/*"
                                        className="d-none"
                                        id="publisherUploadFileInput"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            if (file.size > MAX_FILE_SIZE) {
                                                setUploadForm((prev) => ({ ...prev, error: "File size should not exceed 10 MB" }));
                                                e.target.value = "";
                                                return;
                                            }
                                            setUploadForm((prev) => ({ ...prev, invoiceFile: file, error: null }));
                                            e.target.value = "";
                                        }}
                                    />
                                </div>
                                {uploadForm.error && (
                                    <small style={{ color: "#fb6340", marginTop: 5 }}>{uploadForm.error}</small>
                                )}
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsUploadModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="filter-btn-gradient-primary btn-md responsive"
                            onClick={async () => {
                                if (!uploadForm.invoiceNumber) { notify("danger", "Please enter invoice number"); return; }
                                if (!uploadForm.invoiceFile) { notify("danger", "Please select an invoice file"); return; }
                                try {
                                    setIsLoading(true);
                                    const formData = new FormData();
                                    formData.append("file", uploadForm.invoiceFile);
                                    const invoiceData = {
                                        ledgerID: selectedLedger?.id,
                                        invoiceNumber: uploadForm.invoiceNumber,
                                        status: "PENDING",
                                    };
                                    formData.append("invoiceData", JSON.stringify(invoiceData));

                                    const res = await uploadInvoice(formData);
                                    if (res && (res.code === 200 || res.success)) {
                                        notify("success", "Invoice uploaded successfully");
                                        const invoicePath = res.data?.invoicePath || res.data?.InvoicePath || res.data?.path || res.data?.filePath || null;
                                        setDisplayedLedgers(prev => prev.map(l => {
                                            if (l.id === selectedLedger?.id || l.ledgerID === invoiceData.ledgerID) {
                                                return {
                                                    ...l,
                                                    invoiceNumber: uploadForm.invoiceNumber,
                                                    invoicePath: invoicePath || l.invoicePath,
                                                    invoiceStatus: "PENDING",
                                                };
                                            }
                                            return l;
                                        }));
                                        setIsUploadModalOpen(false);
                                            fetchLedgerList(pageNum);
                                    } else {
                                        notify("danger", res?.message || "Failed to upload invoice");
                                    }
                                } catch (err) {
                                    console.error(err);
                                    notify("danger", "Failed to upload invoice");
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            disabled={!uploadForm.invoiceNumber || !uploadForm.invoiceFile}
                        >
                            {isLoading ? <><Spinner size="sm" className="me-1" /> Uploading...</> : "Upload Invoice"}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Rerun Modal */}
                <Modal isOpen={isRerunModalOpen} toggle={() => setIsRerunModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsRerunModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Rerun Ledger</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Alert color="warning">
                            <FaExclamationTriangle className="me-2" />
                            <strong>Warning:</strong> This action will:
                            <ul className="mb-0 mt-1">
                                <li>Void the current invoice(s)</li>
                                <li>Unlock the stats for this month</li>
                                <li>Allow you to re-post with corrections</li>
                            </ul>
                        </Alert>
                        {selectedLedger && (
                            <div className="mt-3">
                                <p><strong>Month:</strong> {selectedLedger.monthDisplay}</p>
                                <p><strong>Current Net Payable:</strong> {formatCurrency(selectedLedger.netPayable)}</p>
                            </div>
                        )}
                        <FormGroup className="mt-3">
                            <Label>Reason for Rerun <span className="text-danger">*</span></Label>
                            <Input type="textarea" placeholder="Explain why this ledger needs to be rerun..." rows={3} />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsRerunModalOpen(false)}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Export Modal */}
                <Modal isOpen={isExportModalOpen} toggle={() => setIsExportModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsExportModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Export Options</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <p>Select export format for your invoices</p>
                        <div className="d-grid gap-3">
                            <Button color="primary" onClick={() => notify("info", "Exporting PDF invoices...")} className="py-3">
                                <FaFilePdf className="me-2" size={24} />
                                <div>
                                    <strong>Invoice PDF</strong>
                                    <div className="small text-white">Download all invoices as PDF</div>
                                </div>
                            </Button>
                            <Button color="success" onClick={() => notify("info", "Exporting Excel...")} className="py-3">
                                <FaFileExcel className="me-2" size={24} />
                                <div>
                                    <strong>Excel Export</strong>
                                    <div className="small text-white">Download financial summary in Excel</div>
                                </div>
                            </Button>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsExportModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
                     <Card>
                    <ReactBsTable
                        title="Ledger & Invoicing"
                        description="View your payments, adjustments, and tax invoices"
                        data={displayedLedgers}
                        showAddbtn={false}
                        columns={columns}
                        keyField="id"
                        renderFilters={filterSection}
                        page={pageNum}
                        sizePerPage={pageSize}
                        onexportCsvBtnClick={() => setIsExportModalOpen(true)}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        showLoader={isLoading}
                        enablePageSizeManagement={false}
                        bordered
                        hover
                    />
                </Card>

            </Container>
        </>
    );
};

export default PublisherLedger;